import React, { useEffect, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableFooter,
} from "@mui/material";
import {
  HoldingsPageShell,
  SortableHeader,
  EmptyTableRow,
  centerBodyCellSx,
  centerHeadCellSx,
  gainLossCellSx,
  holdingsPaginationSx,
} from "./holdingsTableShared";

interface MF {
  id: number;
  folioNo: string;
  schemeName: string;
  investmentDate: string;
  amount: number;
  purchaseNav: number;
  units: number;
  currentNav: number;
  currentDate: string;
  currentVal: number;
  gainNloss: number | null;
  absReturn: string | null;
  xirr: string;
  createdAt: string;
  noOfDays: string;
}

interface Props {
  data: MF[];
  totalPages: number;
  pageSize: number;
  onPageChange: (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => void;
  onPageSizeChange: (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  currentPage: number;
  fetchData: () => void;
}

const MFHoldingsTable: React.FC<Props> = ({
  data,
  onPageChange,
  onPageSizeChange,
}) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredData, setFilteredData] = useState<MF[]>([]);
  const [sortColumn, setSortColumn] = useState<keyof MF | null>(null);
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");

  useEffect(() => {
    let updatedData = data ?? [];

    if (searchTerm) {
      updatedData = updatedData.filter((row) =>
        Object.values(row).some(
          (value) =>
            value != null &&
            value.toString().toLowerCase().includes(searchTerm.toLowerCase())
        )
      );
    }

    if (sortColumn) {
      updatedData = [...updatedData].sort((a, b) => {
        const aVal = a[sortColumn];
        const bVal = b[sortColumn];
        if (aVal == null && bVal == null) return 0;
        if (aVal == null) return 1;
        if (bVal == null) return -1;
        if (aVal < bVal) return sortOrder === "asc" ? -1 : 1;
        if (aVal > bVal) return sortOrder === "asc" ? 1 : -1;
        return 0;
      });
    }

    setFilteredData(updatedData);
  }, [data, searchTerm, sortColumn, sortOrder]);

  const handleSort = (column: keyof MF) => {
    if (sortColumn === column) {
      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortColumn(column);
      setSortOrder("asc");
    }
  };

  const pagedData = filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const sortHead = (label: string, column: keyof MF) => (
    <TableCell sx={centerHeadCellSx}>
      <SortableHeader label={label} onSort={() => handleSort(column)} align="center" />
    </TableCell>
  );

  return (
    <HoldingsPageShell
      title="Mutual Fund"
      subtitle="View mutual fund holdings for this investor."
      searchValue={searchTerm}
      onSearchChange={(value) => {
        setSearchTerm(value);
        setPage(0);
      }}
      searchPlaceholder="Search mutual fund holdings..."
    >
      <TableContainer sx={{ overflowX: "auto" }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              {sortHead("Folio Number", "folioNo")}
              {sortHead("Scheme Name", "schemeName")}
              {sortHead("Date Of Investment", "investmentDate")}
              {sortHead("Invested Amount", "amount")}
              {sortHead("Purchase NAV", "purchaseNav")}
              {sortHead("Units", "units")}
              {sortHead("Current NAV", "currentNav")}
              {sortHead("Current Date", "currentDate")}
              {sortHead("Current Value", "currentVal")}
              {sortHead("Gain/Loss", "gainNloss")}
              {sortHead("No. Of Days", "noOfDays")}
              {sortHead("Absolute Return (%)", "absReturn")}
              {sortHead("XIRR (%)", "xirr")}
            </TableRow>
          </TableHead>

          <TableBody>
            {pagedData.length === 0 ? (
              <TableRow>
                <EmptyTableRow colSpan={12} message="No mutual fund holdings found" />
              </TableRow>
            ) : (
              pagedData.map((row) => (
                <TableRow key={row.id} hover sx={{ "&:hover": { bgcolor: "#FAFAFA" } }}>
                  <TableCell sx={centerBodyCellSx}>{row.folioNo}</TableCell>
                  <TableCell sx={centerBodyCellSx}>{row.schemeName}</TableCell>
                  <TableCell sx={centerBodyCellSx}>{row.investmentDate}</TableCell>
                  <TableCell sx={centerBodyCellSx}>
                    {row.amount ? `Rs.${Number(row.amount).toFixed(2)}` : "-"}
                  </TableCell>
                  <TableCell sx={centerBodyCellSx}>{row.purchaseNav}</TableCell>
                  <TableCell sx={centerBodyCellSx}>{row.units}</TableCell>
                  <TableCell sx={centerBodyCellSx}>{row.currentNav}</TableCell>
                  <TableCell sx={centerBodyCellSx}>{row.currentDate}</TableCell>
                  <TableCell sx={centerBodyCellSx}>
                    {row.currentVal ? `Rs.${Number(row.currentVal).toFixed(2)}` : "-"}
                  </TableCell>
                  <TableCell sx={gainLossCellSx(row.gainNloss)}>
                    {row.gainNloss != null ? `Rs.${Number(row.gainNloss).toFixed(2)}` : "-"}
                  </TableCell>
                  <TableCell sx={centerBodyCellSx}>{row.noOfDays}</TableCell>
                  <TableCell sx={gainLossCellSx(row.absReturn)}>{row.absReturn ?? "-"}</TableCell>
                  <TableCell sx={gainLossCellSx(row.xirr)}>{row.xirr}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>

          <TableFooter>
            <TableRow>
              <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                count={filteredData.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={(_, newPage) => {
                  setPage(newPage);
                  onPageChange(null, newPage);
                }}
                onRowsPerPageChange={(e) => {
                  const next = parseInt(e.target.value, 10);
                  setRowsPerPage(next);
                  setPage(0);
                  onPageSizeChange(e);
                }}
                sx={holdingsPaginationSx}
              />
            </TableRow>
          </TableFooter>
        </Table>
      </TableContainer>
    </HoldingsPageShell>
  );
};

export default MFHoldingsTable;
